import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { getCrmApiBaseUrl } from "../../utils/utils";
import "../../wallet.css";
import { Pill } from "./walletShared";

/**
 * Projects module (finance grouping / order tagging).
 *
 * - Create a project under a partner (partner is reference only).
 * - List projects with their linked-order counts.
 * - Manage a project: link AWBs by pasting a list or bulk-uploading an
 *   Excel/CSV file, view linked orders, and unlink them.
 *
 * Linking an AWB sets project_id/project_name on the order (new_shipment_job_manager),
 * which then shows up in Live View, Billing Orders and their downloads.
 */
export default function Projects() {
  const API_URL = process.env.REACT_APP_API_URL; // driver API, trailing slash
  const PROJECTS_API = `${API_URL}shypvdriverapp/projects`;

  // ------------------------------------------------------------------ state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [banner, setBanner] = useState(null); // { type: 'success'|'error', text }

  // create form
  const [form, setForm] = useState({ partner: null, projectName: "", description: "" });
  const [creating, setCreating] = useState(false);

  // manage panel
  const [activeProject, setActiveProject] = useState(null);
  const [awbText, setAwbText] = useState("");
  const [parsedFileAwbs, setParsedFileAwbs] = useState([]);
  const [fileName, setFileName] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [projectOrders, setProjectOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [clearRows, setClearRows] = useState(false);

  const flash = (type, text) => {
    setBanner({ type, text });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setBanner(null), 6000);
  };

  // ------------------------------------------------------------------ loaders
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(PROJECTS_API);
      setProjects(res.data?.data || []);
    } catch (err) {
      flash("error", "Failed to load projects: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [PROJECTS_API]);

  const fetchPartners = useCallback(async () => {
    try {
      const base = getCrmApiBaseUrl();
      const res = await axios.get(`${base}/v1.0/partner/retrieve_list?page=0`);
      setPartners(res.data?.data || []);
    } catch (err) {
      // Partner is reference-only; a project can still be created without one.
      console.warn("Could not load partners:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchPartners();
  }, [fetchProjects, fetchPartners]);

  const partnerOptions = useMemo(
    () =>
      [...partners]
        .sort((a, b) => (a.pname || "").localeCompare(b.pname || ""))
        .map((p) => ({
          value: p.pid,
          label: `${String(p.pid).padStart(2, "0")} - ${p.pname}`,
          pname: p.pname,
        })),
    [partners]
  );

  // ------------------------------------------------------------------ create
  const createProject = async () => {
    if (!form.projectName.trim()) {
      flash("error", "Project name is required");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        projectName: form.projectName.trim(),
        description: form.description.trim() || null,
        partnerId: form.partner ? form.partner.value : null,
        partnerName: form.partner ? form.partner.pname : null,
      };
      const res = await axios.post(PROJECTS_API, payload);
      if (res.data?.success) {
        flash("success", `Project "${res.data.data.projectName}" created (${res.data.data.projectCode})`);
        setForm({ partner: null, projectName: "", description: "" });
        fetchProjects();
      } else {
        flash("error", res.data?.message || "Failed to create project");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    } finally {
      setCreating(false);
    }
  };

  const deactivateProject = async (project) => {
    if (!window.confirm(`Deactivate project "${project.projectName}"? Linked orders will be unlinked.`)) return;
    try {
      const res = await axios.delete(`${PROJECTS_API}/${project.id}`);
      if (res.data?.success) {
        flash("success", "Project deactivated");
        if (activeProject && activeProject.id === project.id) setActiveProject(null);
        fetchProjects();
      } else {
        flash("error", res.data?.message || "Failed to deactivate");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    }
  };

  // ------------------------------------------------------------------ manage
  const openManage = (project) => {
    setActiveProject(project);
    setAwbText("");
    setParsedFileAwbs([]);
    setFileName("");
    setSelectedOrders([]);
    loadProjectOrders(project.id);
  };

  const loadProjectOrders = async (projectId) => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${PROJECTS_API}/${projectId}/orders`);
      setProjectOrders(res.data?.data || []);
    } catch (err) {
      flash("error", "Failed to load linked orders: " + (err.response?.data?.message || err.message));
    } finally {
      setOrdersLoading(false);
    }
  };

  // Parse a pasted textarea + an uploaded file into a distinct AWB list.
  const collectAwbs = () => {
    const fromText = awbText
      .split(/[\s,;\n\r\t]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const all = [...fromText, ...parsedFileAwbs];
    return Array.from(new Set(all));
  };

  const onFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
        const awbs = [];
        rows.forEach((row, idx) => {
          if (!row || row.length === 0) return;
          const first = row[0];
          if (first === undefined || first === null || String(first).trim() === "") return;
          const val = String(first).trim();
          // skip a header row like "AWB" / "AWB Number"
          if (idx === 0 && /awb|tracking|order|job/i.test(val) && !/\d/.test(val)) return;
          awbs.push(val);
        });
        const distinct = Array.from(new Set(awbs));
        setParsedFileAwbs(distinct);
        flash("success", `${distinct.length} AWB(s) read from ${file.name}`);
      } catch (err) {
        flash("error", "Could not read file: " + err.message);
        setParsedFileAwbs([]);
      }
    };
    reader.readAsBinaryString(file);
  };

  const assignAwbs = async () => {
    const awbs = collectAwbs();
    if (awbs.length === 0) {
      flash("error", "Paste some AWBs or upload a file first");
      return;
    }
    setAssigning(true);
    try {
      const res = await axios.post(`${PROJECTS_API}/${activeProject.id}/assign`, { awbs });
      if (res.data?.success) {
        const r = res.data.data;
        let msg = `Linked ${r.matched} order row(s) from ${r.requested} AWB(s).`;
        if (r.notFound && r.notFound.length) {
          msg += ` ${r.notFound.length} AWB(s) not found: ${r.notFound.slice(0, 10).join(", ")}${r.notFound.length > 10 ? "…" : ""}`;
        }
        flash(r.notFound && r.notFound.length ? "error" : "success", msg);
        setAwbText("");
        setParsedFileAwbs([]);
        setFileName("");
        loadProjectOrders(activeProject.id);
        fetchProjects();
      } else {
        flash("error", res.data?.message || "Assign failed");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    } finally {
      setAssigning(false);
    }
  };

  const unassignSelected = async () => {
    if (selectedOrders.length === 0) return;
    const awbs = Array.from(new Set(selectedOrders.map((o) => o.awb)));
    try {
      const res = await axios.post(`${PROJECTS_API}/${activeProject.id}/unassign`, { awbs });
      if (res.data?.success) {
        flash("success", `Unlinked ${res.data.data.matched} order row(s)`);
        setSelectedOrders([]);
        setClearRows((c) => !c);
        loadProjectOrders(activeProject.id);
        fetchProjects();
      } else {
        flash("error", res.data?.message || "Unassign failed");
      }
    } catch (err) {
      flash("error", err.response?.data?.message || err.message);
    }
  };

  // ------------------------------------------------------------------ columns
  const projectColumns = [
    { name: "Code", selector: (r) => r.projectCode, sortable: true, grow: 1 },
    { name: "Project", selector: (r) => r.projectName, sortable: true, grow: 2 },
    { name: "Partner", selector: (r) => r.partnerName || (r.partnerId ? `#${r.partnerId}` : "—"), sortable: true, grow: 1.5 },
    { name: "Orders", selector: (r) => r.orderCount ?? 0, sortable: true, width: "100px", center: true },
    {
      name: "Status",
      cell: (r) => <Pill tone={r.isActive ? "success" : "neutral"}>{r.isActive ? "Active" : "Inactive"}</Pill>,
      width: "110px",
    },
    {
      name: "Actions",
      cell: (r) => (
        <div style={{ display: "flex", gap: 8, whiteSpace: "nowrap" }}>
          <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => openManage(r)}>Manage</button>
          {r.isActive && (
            <button className="w-btn w-btn-sm w-btn-danger" onClick={() => deactivateProject(r)}>Deactivate</button>
          )}
        </div>
      ),
      width: "230px",
    },
  ];

  const orderColumns = [
    { name: "AWB", selector: (r) => r.awb, sortable: true, grow: 2 },
    { name: "Type", selector: (r) => r.type || "—", sortable: true, width: "110px" },
    { name: "Status", selector: (r) => r.status || "—", sortable: true, grow: 1 },
    { name: "Payment", selector: (r) => r.paymentType || "—", width: "120px" },
    { name: "City", selector: (r) => r.city || "—", grow: 1 },
  ];

  const pendingCount = collectAwbs().length;

  const tableStyles = {
    headRow: { style: { backgroundColor: "#fafbfc", borderBottom: "1px solid #e7eaf0", minHeight: "46px" } },
    headCells: { style: { fontWeight: "700", fontSize: "11.5px", color: "#6b7280", textTransform: "uppercase", letterSpacing: ".4px" } },
    rows: { style: { minHeight: "52px", fontSize: "13.5px" } },
    pagination: { style: { borderTop: "1px solid #e7eaf0", fontSize: "12.5px", color: "#6b7280" } },
  };

  // ------------------------------------------------------------------ render
  return (
    <div className="wallet-page">
      <div className="w-topbar">
        <div className="w-topbar-left">
          <div className="w-icon-badge"><i className="fa fa-folder-open" aria-hidden="true" /></div>
          <div>
            <h2 className="w-title">Projects</h2>
            <p className="w-subtitle">Group orders under a partner project for finance / billing.</p>
          </div>
        </div>
      </div>

      {banner && (
        <div className={`w-banner ${banner.type === "success" ? "w-banner-success" : "w-banner-error"}`}>
          {banner.text}
        </div>
      )}

      {!activeProject ? (
        <>
          {/* Create */}
          <div className="w-card">
            <div className="w-card-header">
              <span className="w-card-title"><i className="fa fa-plus-circle" aria-hidden="true" /> Create project</span>
            </div>
            <div className="w-card-body">
              <div className="w-form-row">
                <div className="w-field" style={{ flex: "1 1 260px" }}>
                  <label className="w-field-label">Partner (reference)</label>
                  <Select
                    options={partnerOptions}
                    value={form.partner}
                    onChange={(opt) => setForm((f) => ({ ...f, partner: opt }))}
                    placeholder="Select partner…"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  />
                </div>
                <div className="w-field" style={{ flex: "1 1 260px" }}>
                  <label className="w-field-label">Project name *</label>
                  <input
                    className="w-input"
                    value={form.projectName}
                    onChange={(e) => setForm((f) => ({ ...f, projectName: e.target.value }))}
                    placeholder="e.g. Emirates Retail Q3"
                  />
                </div>
                <div className="w-field" style={{ flex: "2 1 320px" }}>
                  <label className="w-field-label">Description</label>
                  <input
                    className="w-input"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    placeholder="Optional notes"
                  />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button className="w-btn w-btn-primary" onClick={createProject} disabled={creating}>
                    {creating ? "Creating…" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="w-card">
            <div className="w-card-header">
              <span className="w-card-title"><i className="fa fa-list-alt" aria-hidden="true" /> All projects</span>
              <button className="w-btn w-btn-ghost w-btn-sm" onClick={fetchProjects}>Refresh</button>
            </div>
            <div className="w-card-body">
              <DataTable
                columns={projectColumns}
                data={projects}
                progressPending={loading}
                pagination
                highlightOnHover
                dense
                customStyles={tableStyles}
                noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>No projects yet — create one above.</div>}
              />
            </div>
          </div>
        </>
      ) : (
        /* Manage */
        <>
          <div className="w-card">
            <div className="w-card-body" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <button className="w-crumb" onClick={() => setActiveProject(null)}>
                  <i className="fa fa-arrow-left" aria-hidden="true" /> Back to projects
                </button>
                <div style={{ marginTop: 10 }}>
                  <span className="w-title" style={{ fontSize: 20, marginRight: 12 }}>{activeProject.projectName}</span>
                  <span className="w-id-chip">{activeProject.projectCode}</span>
                </div>
                <div style={{ color: "#64748b", fontSize: 13, marginTop: 2 }}>
                  Partner: {activeProject.partnerName || (activeProject.partnerId ? `#${activeProject.partnerId}` : "—")}
                </div>
              </div>
            </div>
          </div>

          <div className="w-card">
            <div className="w-card-header">
              <span className="w-card-title"><i className="fa fa-link" aria-hidden="true" /> Link AWBs to this project</span>
            </div>
            <div className="w-card-body">
              <div className="w-form-row">
                <div className="w-field" style={{ flex: "1 1 340px" }}>
                  <label className="w-field-label">Paste AWBs (comma / space / newline separated)</label>
                  <textarea
                    className="w-textarea"
                    value={awbText}
                    onChange={(e) => setAwbText(e.target.value)}
                    placeholder={"UB07-260701-0001\nUB07-260701-0002\n…"}
                    rows={6}
                  />
                </div>
                <div className="w-field" style={{ flex: "1 1 260px" }}>
                  <label className="w-field-label">…or bulk upload a file (.xlsx / .xls / .csv)</label>
                  <input type="file" accept=".xlsx,.xls,.csv" onChange={onFileSelected} />
                  {fileName && (
                    <div style={{ fontSize: 13, color: "#334155", marginTop: 8 }}>
                      <strong>{fileName}</strong> — {parsedFileAwbs.length} AWB(s) read
                    </div>
                  )}
                  <div className="w-help-text" style={{ marginTop: 6 }}>
                    First column is used as the AWB. A header row is skipped automatically.
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
                <button className="w-btn w-btn-primary" onClick={assignAwbs} disabled={assigning || pendingCount === 0}>
                  {assigning ? "Linking…" : `Link ${pendingCount || ""} AWB(s)`}
                </button>
                <span style={{ color: "#64748b", fontSize: 13 }}>
                  {pendingCount} distinct AWB(s) ready
                </span>
              </div>
            </div>
          </div>

          <div className="w-card">
            <div className="w-card-header">
              <span className="w-card-title"><i className="fa fa-cubes" aria-hidden="true" /> Linked orders ({projectOrders.length})</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => loadProjectOrders(activeProject.id)}>Refresh</button>
                {selectedOrders.length > 0 && (
                  <button className="w-btn w-btn-sm w-btn-danger" onClick={unassignSelected}>
                    Unlink {selectedOrders.length} selected
                  </button>
                )}
              </div>
            </div>
            <div className="w-card-body">
              <DataTable
                columns={orderColumns}
                data={projectOrders}
                progressPending={ordersLoading}
                pagination
                selectableRows
                onSelectedRowsChange={(s) => setSelectedOrders(s.selectedRows)}
                clearSelectedRows={clearRows}
                highlightOnHover
                dense
                customStyles={tableStyles}
                noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>No orders linked yet.</div>}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
